import {
   CollectionReference,
   DocumentReference,
   Transaction,
   doc,
   serverTimestamp,
} from "firebase/firestore"
import { getDeviceId } from "../firebase/deviceId"
import { queryClient } from "../main"
import { DocumentData, FirestoreDocument } from "../types/firestoreDocument"
import { dexie } from "./localDatabase"
import { logger } from "@/utils/logger"

let enableCheckForStaleQueries = true

function startTransactionBatch() {
   enableCheckForStaleQueries = false
}

function endTransactionBatch() {
   enableCheckForStaleQueries = true
}

function checkForStaleQueries() {
   if (!enableCheckForStaleQueries) {
      return
   }
   const queries = queryClient.getQueryCache().findAll()
   const hasStaleActiveQueries = queries.some((query) => query.isActive() && query.isStale())

   if (hasStaleActiveQueries) {
      throw new Error(
         "Des données ne sont pas à jour. Veuillez rafraîchir la page avant de continuer.",
      )
   }
}

export function runTransactionBatch(fn: () => Promise<void>) {
   checkForStaleQueries()
   startTransactionBatch()
   return fn().finally(() => endTransactionBatch())
}

export const createDocumentTransactionFn = async <T extends FirestoreDocument>(
   transaction: Transaction,
   collectionRef: CollectionReference,
   data: DocumentData<T>,
) => {
   const docRef = doc(collectionRef)

   checkForStaleQueries()

   logger("TX Creating Document", docRef.path, data)

   transaction.set(docRef, {
      ...data,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
      updated_by: await getDeviceId(),
   })

   return docRef.id
}

export const updateDocumentTransactionFn = async <T extends FirestoreDocument>(
   transaction: Transaction,
   docRef: DocumentReference,
   data: Partial<DocumentData<T>>,
) => {
   checkForStaleQueries()

   const localData = await dexie.table(docRef.parent.path).get(docRef.id)

   const docSnap = await transaction.get(docRef)

   if (!docSnap.exists()) {
      throw new Error("Document introuvable sur le serveur")
   }

   if (!localData) {
      throw new Error("Document introuvable localement")
   }

   if (localData.updated_at?.valueOf() !== docSnap.data().updated_at?.toDate().valueOf()) {
      throw new Error("Document désynchronisé. Veuillez rafraîchir la page.")
   }

   logger("TX Updating Document", docRef.path, data)

   transaction.update(docRef, {
      ...data,
      updated_at: serverTimestamp(),
      updated_by: await getDeviceId(),
   })

   return docSnap
}
